import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Badge } from "../../components/ui/badge";
import {
  FileText,
  Search,
  Filter,
  Calendar,
  User,
  Building2,
  RefreshCw,
} from "lucide-react";
import axios from "axios";
import logger from "../../utils/logger";

const ActivityLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [entityTypeFilter, setEntityTypeFilter] = useState("all");
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 50,
    total: 0,
    total_pages: 1,
  });

  useEffect(() => {
    fetchActivityLogs();
  }, [pagination.current_page, searchTerm, actionFilter, entityTypeFilter]);

  const fetchActivityLogs = async () => {
    try {
      const token = localStorage.getItem("superAdminToken");
      const params = {
        page: pagination.current_page,
        limit: pagination.per_page,
        search: searchTerm || undefined,
        action: actionFilter !== "all" ? actionFilter : undefined,
        entity_type: entityTypeFilter !== "all" ? entityTypeFilter : undefined,
      };

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/super-admin/system/logs`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      setLogs(response.data.data);
      setPagination(response.data.pagination);
    } catch (error) {
      logger.error("Error fetching activity logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchActivityLogs();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, current_page: newPage }));
  };

  const getActionBadge = (action) => {
    const actionStyles = {
      login: "bg-green-100 text-green-800",
      logout: "bg-gray-100 text-gray-800",
      create: "bg-blue-100 text-blue-800",
      update: "bg-yellow-100 text-yellow-800",
      delete: "bg-red-100 text-red-800",
      create_user: "bg-purple-100 text-purple-800",
      update_user: "bg-indigo-100 text-indigo-800",
      delete_user: "bg-pink-100 text-pink-800",
      update_user_status: "bg-teal-100 text-teal-800",
    };

    const actionLabels = {
      login: "تسجيل دخول",
      logout: "تسجيل خروج",
      create: "إنشاء",
      update: "تحديث",
      delete: "حذف",
      create_user: "إنشاء مستخدم",
      update_user: "تحديث مستخدم",
      delete_user: "حذف مستخدم",
      update_user_status: "تغيير حالة مستخدم",
    };

    return (
      <Badge className={actionStyles[action] || "bg-gray-100 text-gray-800"}>
        {actionLabels[action] || action}
      </Badge>
    );
  };

  const getEntityTypeBadge = (entityType) => {
    const typeStyles = {
      super_admin: "bg-purple-100 text-purple-800",
      tenant: "bg-blue-100 text-blue-800",
      plan: "bg-green-100 text-green-800",
      subscription: "bg-yellow-100 text-yellow-800",
      payment: "bg-red-100 text-red-800",
      backup: "bg-indigo-100 text-indigo-800",
      system_user: "bg-pink-100 text-pink-800",
    };

    const typeLabels = {
      super_admin: "مدير نظام",
      tenant: "مستأجر",
      plan: "خطة",
      subscription: "اشتراك",
      payment: "دفع",
      backup: "نسخة احتياطية",
      system_user: "مستخدم نظام",
    };

    return (
      <Badge className={typeStyles[entityType] || "bg-gray-100 text-gray-800"}>
        {typeLabels[entityType] || entityType}
      </Badge>
    );
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">سجل النشاطات</h1>
          <p className="text-slate-600 mt-1">
            تتبع جميع الأنشطة في النظام
          </p>
        </div>
        <Button onClick={handleRefresh} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          تحديث
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 h-4 w-4" />
              <Input
                placeholder="بحث في الوصف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="الإجراء" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الإجراءات</SelectItem>
                <SelectItem value="login">تسجيل دخول</SelectItem>
                <SelectItem value="logout">تسجيل خروج</SelectItem>
                <SelectItem value="create">إنشاء</SelectItem>
                <SelectItem value="update">تحديث</SelectItem>
                <SelectItem value="delete">حذف</SelectItem>
                <SelectItem value="create_user">إنشاء مستخدم</SelectItem>
                <SelectItem value="update_user">تحديث مستخدم</SelectItem>
                <SelectItem value="delete_user">حذف مستخدم</SelectItem>
                <SelectItem value="update_user_status">تغيير حالة مستخدم</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={entityTypeFilter} onValueChange={setEntityTypeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="نوع الكيان" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأنواع</SelectItem>
                <SelectItem value="super_admin">مدير نظام</SelectItem>
                <SelectItem value="tenant">مستأجر</SelectItem>
                <SelectItem value="plan">خطة</SelectItem>
                <SelectItem value="subscription">اشتراك</SelectItem>
                <SelectItem value="payment">دفع</SelectItem>
                <SelectItem value="backup">نسخة احتياطية</SelectItem>
                <SelectItem value="system_user">مستخدم نظام</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              تصفية
            </Button>
            
            <Button variant="outline" className="flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              نطاق زمني
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            سجل الأنشطة
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mb-2"></div>
                <p className="text-slate-600">جاري تحميل سجل النشاطات...</p>
              </div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-1">لا توجد أنشطة</h3>
              <p className="text-slate-600">لا توجد أنشطة مسجلة تطابق الفلاتر الخاصة بك</p>
            </div>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>الإجراء</TableHead>
                      <TableHead>نوع الكيان</TableHead>
                      <TableHead>الوصف</TableHead>
                      <TableHead>المستخدم</TableHead>
                      <TableHead>المستأجر</TableHead>
                      <TableHead>عنوان IP</TableHead>
                      <TableHead>التاريخ</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>{getActionBadge(log.action)}</TableCell>
                        <TableCell>{getEntityTypeBadge(log.entity_type)}</TableCell>
                        <TableCell className="max-w-xs truncate">{log.description}</TableCell>
                        <TableCell>
                          {log.user_name ? (
                            <div>
                              <div className="font-medium">{log.user_name}</div>
                              <div className="text-sm text-slate-500">{log.user_email}</div>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>
                          {log.tenant_name ? (
                            <div>
                              <div className="font-medium">{log.tenant_name}</div>
                              <div className="text-sm text-slate-500">{log.tenant_subdomain}</div>
                            </div>
                          ) : (
                            "N/A"
                          )}
                        </TableCell>
                        <TableCell>{log.ip_address || "N/A"}</TableCell>
                        <TableCell>
                          {new Date(log.created_at).toLocaleString("ar-SA")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex items-center justify-between py-4">
                  <div className="text-sm text-slate-600">
                    إظهار {logs.length} من أصل {pagination.total} سجل
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page - 1)}
                      disabled={pagination.current_page === 1}
                    >
                      السابق
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.current_page + 1)}
                      disabled={pagination.current_page === pagination.total_pages}
                    >
                      التالي
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ActivityLogs;